import React, { useEffect, useRef, useState } from 'react';
import axios from "axios"
import { useLocation } from "react-router-dom"
import { jwtDecode } from "jwt-decode"
import { API_BASE_URL } from "../config"
import Loader from "../components/Loader"
import "./BusinessProfileNew.css"
import Button from "./ui/Button"
import Card from "./ui/Card"
import Badge from "./ui/Badge"
import Avatar from "./ui/Avatar"
import Dialog from "./ui/Dialog"
import ScrollArea from "./ui/ScrollArea"

const BusinessProfileNew = () => {
  const [showAllReviews, setShowAllReviews] = useState(false)
  const [selectedPortfolioItem, setSelectedPortfolioItem] = useState(null)
  const [businessProfile, setBusinessProfile] = useState(null)
  const [certifications, setCertifications] = useState([])
  const [qualifications, setQualifications] = useState([])
  const [services, setServices] = useState([])
  const [portfolio, setPortfolio] = useState([])
  const [reviews, setReviews] = useState([])
  const [jobsCompleted, setJobsCompleted] = useState(0)
  const [jobsQuote, setJobsQuote] = useState(0)
  const [conversationsCount, setConversationsCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const businessIdFromQuery = queryParams.get("businessId")

  useEffect(() => {
    const fetchBusinessProfile = async () => {
      try {
        setLoading(true)
        let businessId
        const token = localStorage.getItem("token")
        const decodedToken = token ? jwtDecode(token) : null
        const decodedBusinessId = businessIdFromQuery ? atob(businessIdFromQuery) : null

        if (decodedToken && decodedToken.BusinessId !== "0" && decodedBusinessId == null) {
          businessId = decodedToken.BusinessId
        } else {
          businessId = decodedBusinessId
        }

        const loggedInUserId = decodedToken ? decodedToken.nameid : null

        const response = await axios.get(
          `${API_BASE_URL}/api/BusinessProfile/getBusinessProfile?businessId=${businessId}&userId=${loggedInUserId}`
        )

        const data = response.data

        setBusinessProfile(data.businesses)
        setCertifications(data.certifications)
        setQualifications(data.qualificationDtos)
        setServices(data.services)
        setPortfolio(data.workPortfolio.model)
        setReviews(data.reviewOfCustomer)
        setJobsCompleted(data.jobsCompleted)
        setJobsQuote(data.jobsQuote)
        setConversationsCount(data.conversation)
      } catch (err) {
        console.error("Failed to fetch business profile:", err)
        // Optionally show a toast or error message to the user
      } finally {
        setLoading(false)
      }
    }

    fetchBusinessProfile()
  }, [businessIdFromQuery])

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <span key={i} className={`star ${i < Math.floor(rating) ? "filled" : "empty"}`}>
        ★
      </span>
    ))
  }

  const getDirections = () => {
    if (businessProfile) {
      const address = encodeURIComponent(
        `${businessProfile.streetNumber} ${businessProfile.streetName}, ${businessProfile.suburb}, ${businessProfile.myState} ${businessProfile.postCode}`
      )
      const url = `https://www.google.com/maps/dir/?api=1&destination=${address}`
      window.open(url, "_blank")
    }
  }

  const openGoogleMaps = () => {
    if (businessProfile) {
      const address = encodeURIComponent(
        `${businessProfile.streetNumber} ${businessProfile.streetName}, ${businessProfile.suburb}, ${businessProfile.myState} ${businessProfile.postCode}`
      )
      const url = `https://www.google.com/maps/search/?api=1&query=${address}`
      window.open(url, "_blank")
    }
  }

  if (loading) {
    return <Loader />
  }

  if (!businessProfile) {
    return <div className="business-profile">No business profile found.</div>
  }

  return (
    <div className="business-profile">
      {/* Header Section */}
      <div className="header-section">
        <div className="container">
          <div className="header-content">
            <Avatar src={businessProfile.profilePic || "/placeholder.svg?height=96&width=96"} alt={businessProfile.businessName} fallback={businessProfile.businessName ? businessProfile.businessName.charAt(0) : "NE"} size="large" />

            <div className="header-info">
              <div className="header-main">
                <div className="business-details">
                  <h1 className="business-name-p">{businessProfile.businessName}</h1>
                  <div className="location">
                    <span className="icon">📍</span>
                    <span>
                      {businessProfile.streetNumber} {businessProfile.streetName}, {businessProfile.suburb},{" "}
                      {businessProfile.myState} {businessProfile.postCode}
                    </span>
                  </div>
                  <div className="rating">
                    <div className="stars">
                      {renderStars(businessProfile.overallRating)}
                      <span className="rating-number">{businessProfile.overallRating}</span>
                      <span className="review-count">({reviews.length} reviews)</span>
                    </div>
                  </div>
                </div>

                <div className="header-actions">
                  <Button className="call-button-p" onClick={() => (window.location.href = `tel:${businessProfile.phone}`)}>
                    📞 Call Now: {businessProfile.phone}
                  </Button>
                  <Button variant="outline" className="save-button">
                    🔖 Save
                  </Button>
                  <Button variant="outline" className="share-button">
                    📤
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container main-content">
        <div className="content-grid">
          {/* Main Content */}
          <div className="main-column">
            {/* Description */}
            <Card>
              <div className="card-header">
                <h2 className="card-title">💬 About Our Services</h2>
              </div>
              <div className="card-content">
                <p className="description">{businessProfile.description || "No description available."}</p>
              </div>
            </Card>

            {/* Services */}
            <Card>
              <div className="card-header">
                <h2 className="card-title">✅ Services Offered</h2>
              </div>
              <div className="card-content">
                <div className="services-grid">
                  {services.length > 0 ? (
                    services.map((service) => (
                      <div key={service.serviceId} className="service-item">
                        <span className="service-icon">✅</span>
                        <span className="service-name">{service.name}</span>
                      </div>
                    ))
                  ) : (
                    <p>No services offered.</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Work Portfolio */}
            <Card>
              <div className="card-header">
                <div className="card-title-row">
                  <h2 className="card-title">📈 Work Portfolio</h2>
                  <Button variant="outline" size="small">
                    Add Portfolio
                  </Button>
                </div>
              </div>
              <div className="card-content">
                <div className="portfolio-grid">
                  {portfolio.length > 0 ? (
                    portfolio.map((item) => (
                      <Card key={item.portfolioId} className="portfolio-item" onClick={() => setSelectedPortfolioItem(item)}>
                        <div className="portfolio-image">
                          <img src={item.mediaUrl && item.mediaUrl.length > 0 ? item.mediaUrl[0].url : "/placeholder.svg"} alt={item.title} />
                          <Badge className="portfolio-badge">{item.suburb}</Badge>
                        </div>
                        <div className="portfolio-content">
                          <h3 className="portfolio-title">{item.title}</h3>
                          <p className="portfolio-description">{item.description}</p>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <p>No portfolio items found.</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Reviews */}
            <Card>
              <div className="card-header">
                <div className="card-title-row">
                  <h2 className="card-title">⭐ Customer Reviews ({reviews.length})</h2>
                  <Button variant="outline" size="small" onClick={() => setShowAllReviews(true)}>
                    View All Reviews →
                  </Button>
                </div>
              </div>
              <div className="card-content">
                <div className="reviews-list">
                  {reviews.length > 0 ? (
                    reviews.slice(0, 3).map((review) => (
                      <div key={review.reviewId} className="review-item">
                        <div className="review-header">
                          <div className="reviewer-info">
                            <Avatar
                              src={review.userProfileImage || "/placeholder.svg"}
                              fallback={review.userName
                                ? review.userName
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                : "U"}
                              size="small"
                            />
                            <span className="reviewer-name">{review.userName}</span>
                          </div>
                          <div className="review-meta">
                            <div className="review-stars">{renderStars(review.overallRating)}</div>
                            <span className="review-date">{new Date(review.dateModified).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <p className="review-comment">{review.description}</p>
                      </div>
                    ))
                  ) : (
                    <p>No customer reviews found.</p>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="sidebar">
            {/* Contact Information */}
            <Card>
              <div className="card-header">
                <h3 className="card-title">Contact Information</h3>
              </div>
              <div className="card-content">
                <div className="contact-list">
                  <div className="contact-item">
                    <span className="contact-icon">📞</span>
                    <a href={`tel:${businessProfile.phone}`}>{businessProfile.phone}</a>
                  </div>
                  <div className="contact-item">
                    <span className="contact-icon">✉️</span>
                    <a href={`mailto:${businessProfile.email}`}>{businessProfile.email}</a>
                  </div>
                  <div className="contact-item">
                    <span className="contact-icon">🌐</span>
                    <a href={`https://${businessProfile.website}`} target="_blank" rel="noopener noreferrer">
                      {businessProfile.website} ↗
                    </a>
                  </div>
                  <div className="contact-item">
                    <span className="contact-icon">📍</span>
                    <span>
                      {businessProfile.streetNumber} {businessProfile.streetName}, {businessProfile.suburb},{" "}
                      {businessProfile.myState} {businessProfile.postCode}
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Google Map */}
            <Card>
              <div className="card-header">
                <h3 className="card-title">Location</h3>
              </div>
              <div className="card-content">
                <div className="map-container">
                  <iframe
                    src={`https://www.google.com/maps/embed/v1/place?key=YOUR_API_KEY&q=${encodeURIComponent(
                      `${businessProfile.streetNumber} ${businessProfile.streetName}, ${businessProfile.suburb}, ${businessProfile.myState} ${businessProfile.postCode}`
                    )}`}
                    width="100%"
                    height="200"
                    style={{ border: 0, borderRadius: "8px" }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Business Location"
                    onError={(e) => {
                      // Fallback to static map if API key is not available
                      e.target.src = `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(
                        `${businessProfile.streetNumber} ${businessProfile.streetName}, ${businessProfile.suburb}, ${businessProfile.myState} ${businessProfile.postCode}`
                      )}&zoom=15&size=300x200&markers=color:red%7C${encodeURIComponent(
                        `${businessProfile.streetNumber} ${businessProfile.streetName}, ${businessProfile.suburb}, ${businessProfile.myState} ${businessProfile.postCode}`
                      )}&key=YOUR_API_KEY`
                    }}
                  ></iframe>
                  <Button className="directions-button" onClick={getDirections}>
                    🧭 Get Directions
                  </Button>
                </div>
              </div>
            </Card>

            {/* Business Stats */}
            <Card>
              <div className="card-header">
                <h3 className="card-title">Business Statistics</h3>
              </div>
              <div className="card-content">
                <div className="stats-grid">
                  <div className="stat-item stat-green">
                    <div className="stat-icon">✅</div>
                    <div className="stat-number">{jobsCompleted}</div>
                    <div className="stat-label">Jobs Complete</div>
                  </div>
                  <div className="stat-item stat-blue">
                    <div className="stat-icon">💬</div>
                    <div className="stat-number">{jobsQuote}</div>
                    <div className="stat-label">Quotes</div>
                  </div>
                  <div className="stat-item stat-purple">
                    <div className="stat-icon">👥</div>
                    <div className="stat-number">{conversationsCount}</div>
                    <div className="stat-label">Messages</div>
                  </div>
                  <div className="stat-item stat-orange">
                    <div className="stat-icon">🕒</div>
                    <div className="stat-number">{businessProfile.yearsExperience}+ Years</div>
                    <div className="stat-label">Experience</div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Business Offers */}
            <Card>
              <div className="card-header">
                <h3 className="card-title">Business Highlights</h3>
              </div>
              <div className="card-content">
                <div className="highlights-list">
                  {businessProfile.fullyInsured && (
                    <div className="highlight-item highlight-green">
                      <span className="highlight-icon">🛡️</span>
                      <span>Fully Insured</span>
                    </div>
                  )}
                  {businessProfile.yearsExperience > 0 && (
                    <div className="highlight-item highlight-blue">
                      <span className="highlight-icon">🏆</span>
                      <span>{businessProfile.yearsExperience}+ Years Experience</span>
                    </div>
                  )}
                  {businessProfile.licenseNumber && (
                    <div className="highlight-item highlight-purple">
                      <span className="highlight-icon">✅</span>
                      <span>Licensed Professional</span>
                    </div>
                  )}
                  {businessProfile.residential === 1 && (
                    <div className="highlight-item highlight-green">
                      <span className="highlight-icon">🏠</span>
                      <span>Residential Services</span>
                    </div>
                  )}
                  {businessProfile.commercial === 1 && (
                    <div className="highlight-item highlight-blue">
                      <span className="highlight-icon">🏢</span>
                      <span>Commercial Services</span>
                    </div>
                  )}
                  {businessProfile.government === 1 && (
                    <div className="highlight-item highlight-purple">
                      <span className="highlight-icon">🏛️</span>
                      <span>Government Projects</span>
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Certifications */}
            <Card>
              <div className="card-header">
                <h3 className="card-title">🏆 Certifications</h3>
              </div>
              <div className="card-content">
                <div className="certifications-list">
                  {certifications.length > 0 ? (
                    certifications.map((cert) => (
                      <div key={cert.certificationId} className="certification-item">
                        <span className="cert-icon">✅</span>
                        <span>{cert.name}</span>
                      </div>
                    ))
                  ) : (
                    <p>No certifications listed.</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Qualifications */}
            <Card>
              <div className="card-header">
                <h3 className="card-title">📅 Qualifications</h3>
              </div>
              <div className="card-content">
                <div className="qualifications-list">
                  {qualifications.length > 0 ? (
                    qualifications.map((qual) => (
                      <div key={qual.qualificationId} className="qualification-item">
                        <span className="qual-icon">✅</span>
                        <span>{qual.name}</span>
                      </div>
                    ))
                  ) : (
                    <p>No qualifications listed.</p>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* All Reviews Modal */}
      {showAllReviews && (
        <Dialog onClose={() => setShowAllReviews(false)}>
          <div className="modal-header">
            <h2>All Customer Reviews</h2>
            <p>
              {reviews.length} reviews with an average rating of{" "}
              {businessProfile.overallRating ? businessProfile.overallRating.toFixed(1) : "0.0"} stars
            </p>
          </div>
          <ScrollArea className="modal-content">
            <div className="all-reviews-list">
              {reviews.length > 0 ? (
                reviews.map((review) => (
                  <div key={review.reviewId} className="review-item">
                    <div className="review-header">
                      <div className="reviewer-info">
                        <Avatar
                          src={review.userProfileImage || "/placeholder.svg"}
                          fallback={review.userName
                            ? review.userName
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                            : "U"}
                          size="small"
                        />
                        <span className="reviewer-name">{review.userName}</span>
                      </div>
                      <div className="review-meta">
                        <div className="review-stars">{renderStars(review.overallRating)}</div>
                        <span className="review-date">{new Date(review.dateModified).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <p className="review-comment">{review.description}</p>
                  </div>
                ))
              ) : (
                <p>No reviews available.</p>
              )}
            </div>
          </ScrollArea>
        </Dialog>
      )}

      {/* Portfolio Modal */}
      {selectedPortfolioItem && (
        <Dialog onClose={() => setSelectedPortfolioItem(null)}>
          <div className="modal-header">
            <h2>{selectedPortfolioItem.title}</h2>
            <p>{selectedPortfolioItem.suburb}</p>
          </div>
          <div className="modal-content">
            <img
              src={selectedPortfolioItem.mediaUrl && selectedPortfolioItem.mediaUrl.length > 0 ? selectedPortfolioItem.mediaUrl[0].url : "/placeholder.svg"}
              alt={selectedPortfolioItem.title}
              className="portfolio-modal-image"
            />
            <p className="portfolio-modal-description">{selectedPortfolioItem.description}</p>
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default BusinessProfileNew
